import { useLocale } from '@/lib/hooks/use-locale';
import { cn } from '@/lib/utils';
import * as React from 'react';
import Spinner from './spinner';

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
    ({ className, onScroll, ...props }, ref) => (
        <div
            ref={ref}
            className={cn(
                'relative w-full overflow-x-auto rounded-md border border-neutral-100 bg-white',
                className,
            )}
            onScroll={onScroll}
        >
            <table className={cn('w-full caption-bottom divide-y overflow-x-auto')} {...props} />
        </div>
    ),
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
    ({ className, ...props }, ref) => (
        <thead ref={ref} className={cn('sticky top-0 z-20 [&_tr]:border-b', className)} {...props} />
    ),
);
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
    ({ className, ...props }, ref) => (
        <>
            <tbody ref={ref} className={cn('[&_tr:last-child]:border-none', className)} {...props} />
        </>
    ),
);
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
    ({ className, ...props }, ref) => (
        <tfoot
            ref={ref}
            className={cn('bg-primary text-primary-foreground font-medium', className)}
            {...props}
        />
    ),
);
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
    ({ className, onClick, ...props }, ref) => (
        <tr
            ref={ref}
            className={cn(
                'h-14',
                'group border-b border-neutral-100 transition-colors hover:bg-neutral-50/50 data-[state=selected]:bg-neutral-50',
                { 'hover:bg-neutral-50 cursor-pointer': !!onClick },
                className,
            )}
            onClick={onClick}
            {...props}
        />
    ),
);
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
    HTMLTableCellElement,
    React.ThHTMLAttributes<HTMLTableCellElement> & { align?: 'left' | 'center' | 'right' }
>(({ className, align = 'left', ...props }, ref) => (
    <th
        ref={ref}
        className={cn(
            'bg-neutral-25 px-3 align-middle text-neutral-600 [&:has([role=checkbox])]:pr-0 text-sm font-normal',
            {
                'text-left': align === 'left',
                'text-center': align === 'center',
                'text-right': align === 'right',
            },
            className,
        )}
        {...props}
    />
));

TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
    HTMLTableCellElement,
    React.TdHTMLAttributes<HTMLTableCellElement> & {
        highlightOnHover?: boolean;
        align?: 'left' | 'center' | 'right';
    }
>(({ className, align = 'left', highlightOnHover = true, ...props }, ref) => (
    <td
        ref={ref}
        className={cn(
            'text-sm font-normal text-neutral-800',
            'px-3 align-middle [&:has([role=checkbox])]:pr-0',
            highlightOnHover && 'group-hover:bg-neutral-25',
            {
                'text-left': align === 'left',
                'text-center': align === 'center',
                'text-right': align === 'right',
            },
            className,
        )}
        {...props}
    />
));

TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
    ({ className, ...props }, ref) => (
        <caption ref={ref} className={cn('text-muted-foreground mt-4 text-base', className)} {...props} />
    ),
);
TableCaption.displayName = 'TableCaption';

const TableCellLoading = React.forwardRef<
    HTMLTableCellElement,
    React.TdHTMLAttributes<HTMLTableCellElement> & { align?: 'left' | 'center' | 'right' }
>(({ colSpan, className, align = 'left', ...props }, ref) => (
    <TableRow>
        <TableCell colSpan={colSpan} className={cn('h-96 bg-white! text-center', className)} {...props}>
            <span>
                <Spinner />
            </span>
        </TableCell>
    </TableRow>
));

TableCellLoading.displayName = 'TableCellLoading';

const TableCellEmpty = React.forwardRef<
    HTMLTableCellElement,
    React.TdHTMLAttributes<HTMLTableCellElement> & { align?: 'left' | 'center' | 'right' }
>(({ colSpan, className, align = 'left', ...props }, ref) => {
    const { t } = useLocale();

    return (
        <TableRow>
            <TableCell colSpan={colSpan} className={cn('h-96 bg-white! text-center', className)} {...props}>
                <span>{t('empty_table__no_results')}</span>
            </TableCell>
        </TableRow>
    );
});
TableCellEmpty.displayName = 'TableCellEmpty';

export {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableCellEmpty,
    TableCellLoading,
    TableFooter,
    TableHead,
    TableHeader,
    TableRow,
};
